/**
 * host — which kind of address the web app is being served on.
 *
 * The address decides whose broker information a public page may show:
 *   - `<slug>.<platform domain>`  → that brokerage (Home renders its page,
 *     with the ComplianceFooter from admin, in place at "/");
 *   - a tenant's own custom domain → the same, once identity-svc confirms the
 *     domain (useHostBrokerage);
 *   - anything else (app./www./admin./api., the bare platform domain,
 *     localhost, *.pages.dev previews) → no brokerage, so no broker name or
 *     licence is shown anywhere: naming one there would advertise a broker the
 *     visitor is not dealing with.
 *
 * Dev-only test hook: `localStorage["rp.mock.host"]` stands in for the real
 * hostname so the web-mock tier can act as a subdomain or a custom domain. It
 * sits behind `import.meta.env.DEV`, which Vite folds to false in a production
 * build, so the override cannot exist in a shipped bundle.
 */

export const PLATFORM_DOMAIN =
  (import.meta.env.VITE_PLATFORM_DOMAIN as string | undefined) ??
  "newbrainllc.us";

const RESERVED_SUBDOMAINS = new Set(["app", "admin", "api", "www"]);

export function currentHostname(): string {
  if (import.meta.env.DEV) {
    try {
      const override = localStorage.getItem("rp.mock.host");
      if (override) return override.toLowerCase();
    } catch {
      /* storage unavailable — use the real host */
    }
  }
  return window.location.hostname.toLowerCase();
}

/** The brokerage slug when the host is `<slug>.<platform domain>`, else null. */
export function platformSubdomainSlug(hostname: string): string | null {
  if (!hostname.endsWith(`.${PLATFORM_DOMAIN}`)) return null;
  const sub = hostname.slice(0, -(PLATFORM_DOMAIN.length + 1));
  if (!sub || RESERVED_SUBDOMAINS.has(sub) || sub.includes(".")) return null;
  return sub;
}

/**
 * Could this host be a tenant's own custom domain? True for any real-looking
 * domain that is not ours and not a local or preview host. Only identity-svc
 * can say whether it actually is one.
 */
export function isCustomDomainCandidate(hostname: string): boolean {
  if (!hostname.includes(".")) return false; // localhost, bare names
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(hostname) || hostname.includes(":")) {
    return false; // IPv4 / IPv6 literals
  }
  if (
    hostname === PLATFORM_DOMAIN ||
    hostname.endsWith(`.${PLATFORM_DOMAIN}`)
  ) {
    return false; // ours: subdomains are recognised by name, the rest is shared
  }
  if (hostname.endsWith(".localhost") || hostname.endsWith(".pages.dev")) {
    return false; // local dev and Cloudflare preview deploys
  }
  return true;
}
